import { z } from 'zod'

/**
 * Portfolio: arvore Solucao -> Produto -> Modulo -> Funcionalidade -> Acao.
 *
 * A ordem e fixa e NUNCA se inverte (spec 11 e 26). Solucao vem antes de Produto.
 * Um no so pode ter como pai o nivel imediatamente acima; Solucao e a unica raiz.
 */
export const PORTFOLIO_LEVELS = ['SOLUTION', 'PRODUCT', 'MODULE', 'FEATURE', 'ACTION'] as const

export const portfolioLevelSchema = z.enum(PORTFOLIO_LEVELS)
export type PortfolioLevel = z.infer<typeof portfolioLevelSchema>

/** Nivel pai valido de cada nivel. `null` = so pode ser raiz. */
export const PARENT_LEVEL: Record<PortfolioLevel, PortfolioLevel | null> = {
  SOLUTION: null,
  PRODUCT: 'SOLUTION',
  MODULE: 'PRODUCT',
  FEATURE: 'MODULE',
  ACTION: 'FEATURE',
}

/** Nivel filho valido de cada nivel. `null` = folha. */
export const CHILD_LEVEL: Record<PortfolioLevel, PortfolioLevel | null> = {
  SOLUTION: 'PRODUCT',
  PRODUCT: 'MODULE',
  MODULE: 'FEATURE',
  FEATURE: 'ACTION',
  ACTION: null,
}

export const portfolioNodeSchema = z.object({
  id: z.string().uuid(),
  level: portfolioLevelSchema,
  parentId: z.string().uuid().nullable(),
  /** Codigo tecnico unico dentro do tenant, validado inline no formulario. */
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  description: z.string().nullable(),
  statusId: z.string().uuid().nullable(),
  ownerMemberId: z.string().uuid().nullable(),
  companyAreaId: z.string().uuid().nullable(),
  sortOrder: z.number().int(),
  archivedAt: z.coerce.date().nullable(),
})
export type PortfolioNode = z.infer<typeof portfolioNodeSchema>

/**
 * Valida se `child` pode ficar sob `parent`.
 * `parent === null` significa criar como raiz — so Solucao aceita.
 */
export function canNest(child: PortfolioLevel, parent: PortfolioLevel | null): boolean {
  return PARENT_LEVEL[child] === parent
}

/** Nivel que um novo filho de `parent` deve ter, ou null se `parent` e folha. */
export function childLevelOf(parent: PortfolioLevel): PortfolioLevel | null {
  return CHILD_LEVEL[parent]
}

export type PortfolioMoveError =
  | { kind: 'invalid-level'; child: PortfolioLevel; parent: PortfolioLevel | null }
  | { kind: 'self-reference' }
  | { kind: 'cycle'; via: string }

/**
 * Regras de mover um no na arvore (spec 11.2): so aceita pais validos, nunca
 * a si mesmo e nunca um descendente proprio (isso quebraria a arvore).
 *
 * `ancestorIds` e o caminho do candidato a pai ate a raiz, do mais proximo ao
 * mais distante — quem chama ja tem esse caminho carregado para o breadcrumb.
 */
export function validatePortfolioMove(input: {
  nodeId: string
  nodeLevel: PortfolioLevel
  newParentId: string | null
  newParentLevel: PortfolioLevel | null
  newParentAncestorIds: readonly string[]
}): PortfolioMoveError | null {
  if (input.newParentId === input.nodeId) return { kind: 'self-reference' }
  if (!canNest(input.nodeLevel, input.newParentLevel)) {
    return { kind: 'invalid-level', child: input.nodeLevel, parent: input.newParentLevel }
  }
  if (input.newParentAncestorIds.includes(input.nodeId)) {
    return { kind: 'cycle', via: input.newParentId ?? '' }
  }
  return null
}
